import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CreditCard, CheckCircle, AlertCircle, Package } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface LoanDetails {
  id: string;
  loan_fee: number;
  payment_status: string;
  due_date: string;
  borrower_first_name: string;
  borrower_last_name: string;
  borrower_email: string;
  assets: {
    name: string;
    category: string;
    description: string | null;
  } | null;
  owner_pta: {
    id: string;
    name: string;
  } | null;
}

export default function LoanPayment() {
  const { loanId } = useParams<{ loanId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loan, setLoan] = useState<LoanDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentProvider, setPaymentProvider] = useState<string | null>(null);

  useEffect(() => {
    if (loanId) {
      fetchLoanDetails();
    }
  }, [loanId]);

  const fetchLoanDetails = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('asset_loans')
        .select(`
          id,
          loan_fee,
          payment_status,
          due_date,
          borrower_first_name,
          borrower_last_name,
          borrower_email,
          assets (name, category, description),
          owner_pta:ptas!asset_loans_owner_pta_id_fkey (id, name)
        `)
        .eq('id', loanId)
        .single();

      if (fetchError) {
        throw new Error('Loan not found');
      }

      const loanData = data as unknown as LoanDetails;
      setLoan(loanData);

      // Detect payment provider for the lending PTA
      if (loanData.owner_pta?.id) {
        const { data: provider } = await supabase
          .rpc('get_pta_payment_provider', { p_pta_id: loanData.owner_pta.id });
        setPaymentProvider(provider);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load loan details');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!loanId || !loan) return;

    setPaying(true);
    try {
      if (paymentProvider === 'mollie') {
        const { data, error: payError } = await supabase.functions.invoke('mollie-create-payment', {
          body: {
            ptaId: loan.owner_pta?.id,
            paymentType: 'loan_fee',
            amount: loan.loan_fee,
            currency: 'GBP',
            description: `Asset Loan Fee: ${loan.assets?.name || 'Asset'}`,
            referenceId: loanId,
          }
        });

        if (payError) throw payError;
        if (data?.checkoutUrl) {
          window.location.href = data.checkoutUrl;
          return;
        }
      } else if (paymentProvider === 'stripe') {
        const { data, error: payError } = await supabase.functions.invoke('create-loan-payment-checkout', {
          body: { loanId }
        });

        if (payError) throw payError;
        if (data?.url) {
          window.location.href = data.url;
          return;
        }
      } else {
        throw new Error('No payment provider configured for this PTA');
      }
    } catch (err: any) {
      toast({
        title: "Payment Error",
        description: err.message || "Could not initiate payment. Please try again.",
        variant: "destructive"
      });
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-4 text-muted-foreground">Loading loan details...</p>
        </div>
      </div>
    );
  }

  if (error || !loan) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Loan Not Found</h2>
              <p className="text-muted-foreground">
                {error || 'The loan you are looking for does not exist or has been removed.'}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isOverdue = new Date(loan.due_date) < new Date();
  const isPaid = loan.payment_status === 'paid';

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-background p-4 flex items-center justify-center">
      <Card className="max-w-lg w-full shadow-lg">
        <CardHeader className="text-center pb-4">
          <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Package className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">Asset Loan Payment</CardTitle>
          <CardDescription>
            {loan.owner_pta?.name}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Asset Details */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <h3 className="font-semibold text-lg">{loan.assets?.name}</h3>
            <p className="text-sm text-muted-foreground">
              Category: {loan.assets?.category}
            </p>
            {loan.assets?.description && (
              <p className="text-sm text-muted-foreground">{loan.assets.description}</p>
            )}
          </div>

          {/* Borrower Info */}
          <div className="space-y-1">
            <p className="text-sm">
              <span className="text-muted-foreground">Borrower:</span>{' '}
              {loan.borrower_first_name} {loan.borrower_last_name}
            </p>
            <p className="text-sm">
              <span className="text-muted-foreground">Due Date:</span>{' '}
              <span className={isOverdue ? 'text-destructive font-medium' : ''}>
                {new Date(loan.due_date).toLocaleDateString('en-GB', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
                {isOverdue && ' (Overdue)'}
              </span>
            </p>
          </div>

          {/* Payment Status */}
          {isPaid ? (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 text-center">
              <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
              <p className="font-semibold text-green-800 dark:text-green-400">Payment Complete</p>
              <p className="text-sm text-green-700 dark:text-green-500">
                Thank you! Your payment of £{loan.loan_fee.toFixed(2)} has been received.
              </p>
            </div>
          ) : (
            <>
              {/* Payment Amount */}
              <div className="text-center py-4 border-y border-border">
                <p className="text-sm text-muted-foreground mb-1">Amount Due</p>
                <p className="text-4xl font-bold text-primary">
                  £{loan.loan_fee.toFixed(2)}
                </p>
              </div>

              {/* Pay Button */}
              {!paymentProvider ? (
                <div className="text-center text-muted-foreground">
                  <AlertCircle className="h-6 w-6 mx-auto mb-2" />
                  <p>No payment provider configured for this PTA.</p>
                  <p className="text-sm">Please contact the PTA for payment instructions.</p>
                </div>
              ) : (
                <>
                  <Button
                    onClick={handlePayment}
                    disabled={paying}
                    className="w-full h-12 text-lg"
                    size="lg"
                  >
                    {paying ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCard className="h-5 w-5 mr-2" />
                        Pay Now
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-center text-muted-foreground">
                    Secure payment processed directly to {loan.owner_pta?.name}.
                  </p>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
